import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnInit,
  Input,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  Inject,
  PLATFORM_ID,
  InjectionToken,
  HostListener,
  ElementRef,
  ViewChild,
  AfterViewInit,
  inject,
  HostBinding,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import * as fromMenu from '@teamfoster/sdk/menu-ngrx';
import { Store } from '@ngrx/store';
import { CloseSearch, OpenMenu, OpenSearch } from 'src/app/store';
import { Platform } from '@angular/cdk/platform';
import { DomSanitizer } from '@angular/platform-browser';
import { Storefront } from 'src/app/storefronts';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  standalone: false,
})
export class TopbarComponent implements OnInit, AfterViewInit {
  @Input() loading: boolean = false;
  @Input() menuActive: boolean = false;
  @Input() searchOpen: boolean = false;
  @Input() logoClass: string = '';
  @Input() menu: fromMenu.MenuItem[] = [];
  @Input() transparent: boolean = false;
  @Input() cartCount: number = 0;
  @Input() storefront: Storefront | null = null;
  @Input() url?: string;
  // @Input() contentPageMenuItems: ContentPage[];

  @Output() toggleOverlay = new EventEmitter<void>();
  @Output() closeOverlay = new EventEmitter<void>();
  @Output() openOverlay = new EventEmitter<void>();
  @Output() toggleCart = new EventEmitter<void>();

  @ViewChild('topbar') topbarEl?: ElementRef;
  @ViewChild('topbarContainer') ContainerEl?: ElementRef;

  platform = inject(Platform);

  fixed = false;
  setIn = false;
  activeSubmenu = '';
  activeSubmenuMobile = '';
  lastScrollTop = 0;
  topbarScrollAt = 0;
  scrollDirection: 'up' | 'down' = 'up';

  scrollDebounceTime = 10;

  constructor(
    private cd: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: InjectionToken<Object>,
    private router: Router,
    private store: Store,
    private sanitizer: DomSanitizer
  ) {
    router.events.subscribe(s => {
      if (s instanceof NavigationStart) {
        this.closeOverlay.emit();
        this.activeSubmenu = '';
        this.activeSubmenuMobile = '';
        this.store.dispatch(CloseSearch());
      }
      //  this.scrollDebounceTime = this.platform.IOS ? 300 : 10;
    });
  }

  ngOnInit() {
    this.calculateAnimationStartPosition();

    if (isPlatformBrowser(this.platformId)) {
      fromEvent(window, 'scroll')
        .pipe(debounceTime(this.scrollDebounceTime))
        .subscribe(a => {
          const st = window.scrollY > 0 ? window.scrollY : 0;
          if (!this.activeSubmenu.length) {
            this.scrollDirection = st < this.lastScrollTop ? 'up' : 'down';
          }
          this.lastScrollTop = st;

          this.setFixed();
          this.cd.detectChanges();
        });
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.topbarScrollAt = this.ContainerEl?.nativeElement.offsetTop + this.topbarEl?.nativeElement.offsetHeight;
    }

    //setTimeout(() => {
    //  this.store.dispatch(OpenMenu());
    //}, 500);
  }

  setFixed() {
    this.fixed = this.scrollDirection === 'up' ? this.lastScrollTop > 36 : this.lastScrollTop > this.topbarScrollAt;
  }

  get ActiveMenuHasSubmenu() {
    return this.menu.filter(item => item.guid === this.activeSubmenu)[0]?.menuItems?.length;
  }

  get themeLight() {
    return this.transparent && !this.fixed && !this.menuActive;
  }

  testAction(guid: any) {
    this.activeSubmenu = guid;
  }
  toggleMobileMenu() {
    this.activeSubmenu = '';
    this.activeSubmenuMobile = '';

    this.toggleOverlay.emit();
    this.calculateAnimationStartPosition();
  }

  setActiveMobileMenu(page: fromMenu.MenuItem) {
    if (!page?.menuItems.length) {
      if (page.routerLink?.length) {
        this.router.navigate(page.routerLink);
      } else if (page.url && isPlatformBrowser(this.platformId)) {
        window.location.href = page.url;
      }
      return;
    }

    this.activeSubmenuMobile === page.guid ? (this.activeSubmenuMobile = '') : (this.activeSubmenuMobile = page.guid);
    //reset scrollpos
    if (isPlatformBrowser(this.platformId)) {
      document.querySelector('.topbar__expansion-panel')?.scrollTo(0, 0);
    }
  }

  calculateAnimationStartPosition() {
    if (isPlatformBrowser(this.platformId)) {
      const panelRect = document.getElementById('panel')?.getBoundingClientRect();
      const items = document.querySelectorAll('.subnav-list__mobile-toggle');

      this.calculateTopbarHeight();

      items.forEach(menuItem => {
        const itemRect = menuItem.getBoundingClientRect();
        menuItem.setAttribute('style', `--top-offset: ${itemRect.top - (panelRect?.top || 0)}px`);
      });

      this.cd.detectChanges();
    }
  }

  calculateTopbarHeight() {
    const actionbar = document.querySelector('app-action-bar')?.getBoundingClientRect()?.height || 0;
    const bar = document.querySelector('.topbar')?.getBoundingClientRect()?.height || 0;

    const topbarEl: HTMLElement | null = document.querySelector('.topbar-sticky-container');
    topbarEl?.setAttribute('style', `--topbar-total-bar-height: ${Math.round(actionbar + bar)}px`);

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    setTimeout(() => {
      // Force a repaint by accessing the offsetHeight property
      var unusedVariable = (document.querySelector('.topbar__expansion-panel') as HTMLElement | null)?.offsetHeight;
    }, 1);
  }

  routeTo(page: fromMenu.MenuItem, e: MouseEvent) {
    this.toggleMobileMenu();

    if (page.routerLink?.length) {
      this.router.navigate(page.routerLink);
    } else if (page.url) {
      window.open(page.url, '_blank')?.focus();
    }

    e.stopPropagation();
    e.preventDefault();
  }

  @HostListener('document:keyup.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    this.closeOverlay.emit();
    this.store.dispatch(CloseSearch());
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: Event) {
    if (isPlatformBrowser(this.platformId)) {
      this.topbarScrollAt = this.ContainerEl?.nativeElement.offsetTop + this.topbarEl?.nativeElement.offsetHeight;
    }
  }

  openSearch() {
    this.store.dispatch(OpenSearch());
  }

  //@HostBinding('attr.style')
  //public get valueAsStyle(): any {
  //  const actionbar = document.querySelector('app-action-bar')?.getBoundingClientRect()?.height || 0;
  //  const bar = document.querySelector('.topbar')?.getBoundingClientRect()?.height || 0;
  //  return this.sanitizer.bypassSecurityTrustStyle(`--topbar-total-bar-height: ${actionbar + bar}px`);
  //}

  // test util
  repeatLists(arr: fromMenu.MenuItem[]) {
    return [...arr];
    //return [...arr, ...arr];
  }
}
